import { Typography, Box, Divider } from "@mui/material"
import { makeStyles } from "tss-react/mui"
import OrderItemsTable from "../../meetingService/components/slipModal/OrderItemsTable"
import { useDateTime } from "../../../../../localization/useDateTime"
import DetailRow from "../../meetingService/components/deliveryListModal/DetailRow"
import { Stack } from "@mui/system"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { OrderNoteAlert } from "../../components/OrderNoteAlert"
import { useMemo } from "react"
import { Order } from "../../../../controlKitchenTypes"

const useStyles = makeStyles()((theme) => ({
    orderContainer: {
        "&:last-child": {
            marginBottom: 0,
        },
        "@media print": {
            // breakAfter: "page",
            "&:last-child": {
                breakAfter: "auto",
            },
            marginBottom: 0,
            breakInside: "avoid",
        },
    },
    orderDivider: {
        borderStyle: "dashed",
        borderColor: theme.palette.tailwind.slate[700],
    },
    orderId: {
        fontWeight: 500,
        lineHeight: "1.5rem",
        color: theme.palette.tailwind.slate[700],
        fontSize: "1.25rem",
    },
    mainContent: {
        display: "flex",
        gap: theme.spacing(4),
        [theme.breakpoints.down("md")]: {
            flexDirection: "column",
        },
        "@media print": {
            gap: theme.spacing(2),
            display: "flex",
            flexDirection: "row",
            breakInside: "avoid",
        },
    },
    leftColumn: {
        flex: "0 0 40%",
        gap: "1.5rem",
        maxWidth: "40%",
        "@media print": {
            flex: "0 0 50%",
            maxWidth: "50%",
            breakInside: "avoid",
        },
    },
    rightColumn: {
        flex: `0 0 calc(60% - ${theme.spacing(4)})`,
        maxWidth: `calc(60% - ${theme.spacing(4)})`,
        "@media print": {
            flex: `0 0 calc(50% - ${theme.spacing(2)})`,
            maxWidth: `calc(50% - ${theme.spacing(2)})`,
            breakInside: "avoid",
        },
    },
    noteSection: {
        marginTop: "2rem",
    },
}))

interface PosSingleOrderProps {
    order: Order
    itemIndex?: number
    totalOrders: number
}

export default function PosSingleOrder({ order, itemIndex = 0, totalOrders }: PosSingleOrderProps) {
    const { classes } = useStyles()
    const dateTimeFactory = useDateTime()

    const deliveryDateTime = order?.deliveries?.[0].deliveryTime
    const deliveryDate = deliveryDateTime ? dateTimeFactory.toDate(dateTimeFactory.fromApi(deliveryDateTime)) : "-"
    const purchaseTime = order.created ? dateTimeFactory.formatDateTime(dateTimeFactory.fromApi(order.created)) : null

    const mapTableOrders = useMemo(() => {
        return (
            order?.orderLines?.map((orderLine) => ({
                id: orderLine.productId,
                items: orderLine.items,
                name: orderLine.name,
            })) ?? []
        )
    }, [order])

    return (
        <>
            <Box className={classes.orderContainer}>
                <Box className={classes.mainContent}>
                    <Box className={classes.leftColumn}>
                        <Stack gap="1.5rem">
                            <Box>
                                <Typography variant="h6" className={classes.orderId}>
                                    #{order?.id}
                                </Typography>
                            </Box>
                            <Stack gap="0.5rem">
                                <LocalizedStrict id="pos-order-delivery-date" attrs={{ label: true }}>
                                    <DetailRow label="Delivery date:" value={deliveryDate} />
                                </LocalizedStrict>
                                <LocalizedStrict id="pos-order-purchase-time" attrs={{ label: true }}>
                                    <DetailRow label="Purchase time:" value={purchaseTime} />
                                </LocalizedStrict>
                            </Stack>
                            <LocalizedStrict id="pos-order-customer" attrs={{ label: true }}>
                                <DetailRow
                                    label="Customer:"
                                    value={order.customer?.displayName}
                                    secondaryValues={[order.customer?.email]}
                                />
                            </LocalizedStrict>
                        </Stack>
                    </Box>

                    <Box className={classes.rightColumn}>
                        <OrderItemsTable orders={mapTableOrders} hideDeliveryTime={true} />
                        {!!order.orderNote && (
                            <OrderNoteAlert note={order.orderNote} className={classes.noteSection} />
                        )}
                    </Box>
                </Box>
            </Box>

            {itemIndex < totalOrders - 1 && <Divider className={classes.orderDivider} />}
        </>
    )
}
